import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../supabaseClient';


const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <h3 className="text-xl font-bold text-[#28357B]">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, isDeleting }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[110] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-500 mb-6">{message}</p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={isDeleting}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isDeleting}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-2 disabled:opacity-50"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Toast = ({ message, type, show, onClose }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  const bgClass = type === 'success' ? 'bg-green-500' : 'bg-red-500';

  return (
    <div className={`fixed top-4 right-4 ${bgClass} text-white px-6 py-3 rounded shadow-lg z-[120] flex items-center space-x-2 transition-opacity duration-300`}>
      <span>{message}</span>
      <button onClick={onClose} className="text-white hover:text-gray-200">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
    </div>
  );
};

const SubmissionViewer = ({ tableName, title, columns }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, new, read
  const [selectedItem, setSelectedItem] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  // Confirmation & Toast States
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [tableName, filter]);

  const fetchData = useCallback(async () => {
    setLoading(true);

    let query = supabase
      .from(tableName)
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false });

    if (filter !== 'all') {
      query = query.eq('status', filter);
    }

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const { data, error, count } = await query.range(from, to);

    if (error) {
      console.error('Error fetching data:', error);
    } else {
      setData(data);
      setTotalCount(count || 0);
    }
    setLoading(false);
  }, [tableName, filter, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const handleStatusUpdate = async (id, newStatus) => {
    const { error } = await supabase
      .from(tableName)
      .update({ status: newStatus })
      .eq('id', id);

    if (error) {
      showToast('Error updating status', 'error');
    } else {
      fetchData(); // Refresh data to maintain consistency
      if (selectedItem && selectedItem.id === id) {
        setSelectedItem({ ...selectedItem, status: newStatus });
      }
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteConfirm({ show: true, id });
  };

  const handleConfirmDelete = async () => {
    const id = deleteConfirm.id;
    setIsDeleting(true);
    try {
      // Use .select() to verify if the row was actually deleted
      const { data, error } = await supabase
        .from(tableName)
        .delete()
        .eq('id', id)
        .select();

      if (error) {
        showToast(`Delete failed: ${error.message}`, 'error');
        console.error('Delete error details:', error);
      } else if (!data || data.length === 0) {
        // If data is empty, it means the query matched 0 rows (ID not found or RLS blocked delete)
        showToast(`No record was deleted with ID ${id} from table ${tableName}. Check RLS policies.`, 'error');
      } else {
        showToast(`Submission (ID: ${id}) deleted successfully`, 'success');
        fetchData(); // Refresh data
        if (selectedItem?.id === id) setSelectedItem(null);
      }
    } catch (err) {
      console.error('Unexpected delete error:', err);
      showToast('An unexpected error occurred during deletion', 'error');
    } finally {
      setIsDeleting(false);
      setDeleteConfirm({ show: false, id: null });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const FileDownloadLink = ({ bucket, path }) => {
    const [loading, setLoading] = useState(false);

    const handleDownload = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      setLoading(true);
      try {
        const { data, error } = await supabase.storage
          .from(bucket)
          .createSignedUrl(path, 3600); // 1 hour expiry

        if (error) throw error;

        window.open(data.signedUrl, '_blank');
      } catch (err) {
        console.error('Error downloading file:', err);
        showToast('Failed to download file', 'error');
      } finally {
        setLoading(false);
      }
    };

    return (
      <button
        onClick={handleDownload}
        className="text-blue-600 hover:underline text-sm flex items-center gap-1"
        disabled={loading}
      >
        {loading ? 'Opening...' : 'View Document'}
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </button>
    );
  };

  if (loading && page === 1) return <div className="p-8">Loading...</div>;

  return (
    <div className="p-8">
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />

      <ConfirmationModal
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, id: null })}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this submission? This action cannot be undone."
        isDeleting={isDeleting}
      />

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#28357B]">{title}</h2>
        <div className="space-x-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border rounded px-3 py-1"
          >
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="read">Read</option>
            <option value="replied">Replied</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded shadow overflow-hidden border border-gray-200">
        <div className="overflow-x-auto overflow-y-auto max-h-[75vh]">
          <table className="min-w-full divide-y divide-gray-200 relative">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                {columns.map((col) => (
                  <th key={col.key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {col.label}
                  </th>
                ))}
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedItem(item)}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(item.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                    ${item.status === 'new' ? 'bg-green-100 text-green-800' :
                        item.status === 'read' ? 'bg-gray-100 text-gray-800' :
                          'bg-blue-100 text-blue-800'}`}>
                      {item.status}
                    </span>
                  </td>
                  {columns.map((col) => (
                    <td key={col.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                      {col.type === 'link' && item[col.key] ? (
                        <a href={item[col.key]} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" onClick={(e) => e.stopPropagation()}>
                          Open Link
                        </a>
                      ) : col.type === 'file' && item[col.key] ? (
                        <FileDownloadLink bucket={col.bucket} path={item[col.key]} />
                      ) : col.type === 'datetime' && item[col.key] ? (
                        formatDate(item[col.key])
                      ) : (
                        item[col.key]
                      )}
                    </td>
                  ))}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button onClick={(e) => { e.stopPropagation(); setSelectedItem(item); }} className="text-indigo-600 hover:text-indigo-900 mr-4">View</button>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteClick(item.id); }} className="text-red-600 hover:text-red-900">Delete</button>
                  </td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 3} className="px-6 py-12 text-center text-gray-500">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <span className="text-sm text-gray-700">
            Showing <span className="font-medium">{totalCount === 0 ? 0 : (page - 1) * pageSize + 1}</span> to <span className="font-medium">{Math.min(page * pageSize, totalCount)}</span> of <span className="font-medium">{totalCount}</span> results
          </span>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className={`px-3 py-1 border rounded text-sm ${page === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white hover:bg-gray-50 text-gray-700'}`}
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(Math.ceil(totalCount / pageSize), p + 1))}
              disabled={page >= Math.ceil(totalCount / pageSize)}
              className={`px-3 py-1 border rounded text-sm ${page >= Math.ceil(totalCount / pageSize) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white hover:bg-gray-50 text-gray-700'}`}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        title="Submission Details"
      >
        {selectedItem && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500">Date</label>
                <p className="font-medium">{formatDate(selectedItem.created_at)}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Status</label>
                <select
                  value={selectedItem.status}
                  onChange={(e) => handleStatusUpdate(selectedItem.id, e.target.value)}
                  className="ml-2 border rounded px-2 py-1 text-sm"
                >
                  <option value="all">Select...</option>
                  <option value="new">New</option>
                  <option value="read">Read</option>
                  <option value="replied">Replied</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>
            </div>

            {columns.map(col => (
              <div key={col.key}>
                <label className="text-sm text-gray-500">{col.label}</label>
                {col.type === 'link' && selectedItem[col.key] ? (
                  <p>
                    <a href={selectedItem[col.key]} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                      {selectedItem[col.key]}
                    </a>
                  </p>
                ) : col.type === 'file' && selectedItem[col.key] ? (
                  <div className="mt-1">
                    <FileDownloadLink bucket={col.bucket} path={selectedItem[col.key]} />
                  </div>
                ) : col.type === 'datetime' && selectedItem[col.key] ? (
                  <p className="font-medium">{formatDate(selectedItem[col.key])}</p>
                ) : col.type === 'long-text' ? (
                  <p className="bg-gray-50 p-3 rounded whitespace-pre-wrap">{selectedItem[col.key]}</p>
                ) : (
                  <p className="font-medium break-words">{selectedItem[col.key]}</p>
                )}
              </div>
            ))}
            
            <div className="pt-6 border-t flex justify-end gap-3">
              <button
                onClick={() => handleDeleteClick(selectedItem.id)}
                className="text-red-600 hover:bg-red-50 px-4 py-2 rounded transition-colors"
                disabled={isDeleting}
              >
                Delete
              </button>
              <button
                onClick={() => setSelectedItem(null)}
                className="bg-gray-100 text-gray-800 px-4 py-2 rounded hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SubmissionViewer;
